import { useState, useEffect } from 'react';
import {
  createFileRoute,
  useParams,
  useNavigate,
} from '@tanstack/react-router';
import { useSelector, useDispatch } from 'react-redux';
import moment from 'moment';

import { ProtectedRoute } from '@/components/protected-route';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';

import { selectJobOverviewData, selectBookingByJobIdAndDate, selectCheckinByBooking } from '@/store/selectors';
import { checkIn } from '@/store/actions';
import { updateHelpModal, updateNotesModal } from '@/store/slices/appStateSlice';
import type { RootState } from '@/store';

export const Route = createFileRoute('/job/$jobId/$date/checkin')({
  component: () => (
    <ProtectedRoute>
      <JobCheckInPage />
    </ProtectedRoute>
  ),
});

function JobCheckInPage() {
  const { jobId, date } = useParams({ from: '/job/$jobId/$date/checkin' });
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [currentLocation, setCurrentLocation] = useState<{
    latitude: number;
    longitude: number;
  } | null>(null);
  const [locationError, setLocationError] = useState<string | null>(null);

  // Get job data from Redux - use date-aware booking lookup
  const { job, jobState } = useSelector((state: RootState) =>
    selectJobOverviewData(state, jobId)
  );
  const booking = useSelector((state: RootState) =>
    selectBookingByJobIdAndDate(state, jobId, date)
  );

  const checkinToday = useSelector((state: RootState) =>
    booking ? selectCheckinByBooking(state, booking.id, booking.date) : null
  );

  useEffect(() => {
    // Get current location
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setCurrentLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          });
          setLocationError(null);
        },
        (error) => {
          console.error('Geolocation error:', error);

          // Handle specific error types
          switch (error.code) {
            case error.PERMISSION_DENIED:
              setLocationError('Location access denied. Please enable location permissions and refresh.');
              break;
            case error.POSITION_UNAVAILABLE:
              setLocationError('Location information unavailable. Please try again or check your GPS.');
              break;
            case error.TIMEOUT:
              setLocationError('Location request timed out. Please try again.');
              break;
            default:
              setLocationError('Unable to get your location. Please try again.');
              break;
          }

          // Try with lower accuracy as fallback
          navigator.geolocation.getCurrentPosition(
            (position) => {
              setCurrentLocation({
                latitude: position.coords.latitude,
                longitude: position.coords.longitude,
              });
              setLocationError(null);
            },
            (fallbackError) => {
              console.error('Fallback geolocation also failed:', fallbackError);
            },
            {
              enableHighAccuracy: false,
              timeout: 15000,
              maximumAge: 300000, // 5 minutes
            }
          );
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 60000,
        }
      );
    } else {
      setLocationError('Geolocation is not supported by this browser');
    }
  }, []);

  const handleGoBack = () => {
    navigate({ to: '/job/$jobId/$date', params: { jobId, date } });
  };

  const retryLocation = () => {
    setLocationError(null);
    setCurrentLocation(null);

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setCurrentLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          });
          setLocationError(null);
        },
        (error) => {
          console.error('Geolocation retry error:', error);

          switch (error.code) {
            case error.PERMISSION_DENIED:
              setLocationError('Location access denied. Please enable location permissions in your browser settings.');
              break;
            case error.POSITION_UNAVAILABLE:
              setLocationError('Location information unavailable. Please check your GPS/location services.');
              break;
            case error.TIMEOUT:
              setLocationError('Location request timed out. Please try again.');
              break;
            default:
              setLocationError('Unable to get your location. Please try again.');
              break;
          }
        },
        {
          enableHighAccuracy: false,
          timeout: 15000,
          maximumAge: 0, // Force fresh location
        }
      );
    }
  };

  const handleCheckIn = async () => {
    if (!booking || checkinToday) return;

    const checkInData = {
      checked_in_at: moment().format('YYYY-MM-DD HH:mm:ss'),
      checked_out_at: null,
      checked_in_location: currentLocation,
      created_at: moment().format('YYYY-MM-DD HH:mm:ss'),
      updated_at: moment().format('YYYY-MM-DD HH:mm:ss'),
      booking_id: booking.id,
      booking_date: booking.date,
      company_id: booking.company_id,
    };

    dispatch(checkIn(checkInData));

    // Navigate to next step based on job state
    if (jobState?.confirmed_walkthrough) {
      // Skip walkthrough if already done - check if we need protective photos
      if (!jobState?.confirmed_protective_photos) {
        navigate({ to: '/job/$jobId/$date/protective-photos', params: { jobId, date } });
      } else {
        navigate({ to: '/job/$jobId/$date/photos', params: { jobId, date } });
      }
    } else {
      navigate({ to: '/job/$jobId/$date/walkthrough', params: { jobId, date } });
    }
  };

  const showNotesModal = () => {
    dispatch(updateNotesModal({
      show: true,
      canAssignTask: true,
    }));
  };

  const showHelpModal = () => {
    dispatch(updateHelpModal(true));
  };

  if (!job || !booking) {
    return (
      <div className="col-span-2 flex items-center justify-center h-full">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-600">Job not found</h2>
          <p className="text-gray-500">Could not load job data</p>
        </div>
      </div>
    );
  }

  return (
    <ScrollArea className="col-span-2 h-full overflow-y-auto overflow-x-hidden max-h-[calc(100vh-5rem)]">
      <div className="p-8 flex justify-center">
        <div className="max-w-4xl w-full space-y-8">

          <div className="space-y-4">
            <h1 className="text-3xl font-bold leading-tight m-0">
              {!checkinToday
                ? 'Check in to the job'
                : 'You have already Checked In Today'}
            </h1>

            {!checkinToday && (
              <div className="mt-4">
                <p className="text-base leading-relaxed">
                  Location verification required to confirm arrival at:
                </p>
                {!currentLocation && locationError && (
                  <div className="mt-2 p-2 bg-red-50 border-l-4 border-red-400 rounded">
                    <p className="text-red-700 text-sm font-medium">
                      ⚠️ Enable location services to proceed with check-in
                    </p>
                  </div>
                )}
              </div>
            )}

            <p className="text-base leading-relaxed text-black m-0">
              {job.formatted_address}
            </p>

            <p className="text-black mb-4 flex items-center">
              Booking time: {moment(booking.starts_at).format('HH:mma')}
              <span className="mx-2">→</span>
              {!checkinToday
                ? `Current time ${moment().format('HH:mma')}`
                : `Checked In time ${moment(checkinToday.checked_in_at).format('HH:mma')}`}
            </p>
          </div>

          <div className="space-y-4">
            <Label className="text-gray-800 font-semibold text-sm uppercase">
              Location Verification Required
            </Label>

            <Card className="h-80 p-0 overflow-hidden">
              <CardContent className="p-0 h-full">

                {locationError ? (
                  <div className="flex flex-col items-center justify-center h-full p-4 text-center">
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                      <p className="text-red-700 font-medium mb-2">⚠️ Location Required</p>
                      <p className="text-red-600 text-sm mb-3">{locationError}</p>

                      <div className="text-left text-sm text-red-600 space-y-2 mb-4">
                        <p className="font-medium">To enable location:</p>
                        <ol className="list-decimal list-inside space-y-1 ml-2">
                          <li>Check browser location permissions</li>
                          <li>Enable GPS/Location Services on device</li>
                          <li>Ensure you're not in an area with poor signal</li>
                        </ol>
                      </div>

                      <div className="flex flex-col gap-2">
                        <Button
                          onClick={retryLocation}
                          variant="outline"
                          size="sm"
                        >
                          Retry Location
                        </Button>
                        <p className="text-xs text-red-500 font-medium">
                          If location still fails, call the office for assistance
                        </p>
                      </div>
                    </div>
                  </div>
                ) : currentLocation ? (
                  <div className="flex items-center justify-center h-full bg-gray-100">
                    <iframe
                      src={`https://www.google.com/maps/embed/v1/place?key=${import.meta.env.VITE_GOOGLE_MAPS_API_KEY}&q=${currentLocation.latitude},${currentLocation.longitude}`}
                      width="100%"
                      height="100%"
                      allowFullScreen
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                    />
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-full">
                    <p className="text-gray-500">Getting your location...</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          <div className="flex gap-4 py-8 border-t border-gray-200">
            <Button
              onClick={!checkinToday ? handleCheckIn : handleGoBack}
              className="flex-1"
              disabled={!checkinToday && !currentLocation}
            >
              {!checkinToday
                ? (currentLocation
                    ? 'Check In and Proceed with Job'
                    : 'Location Required to Check In')
                : `Checked In time ${moment(checkinToday.checked_in_at).format('HH:mma')}`}
            </Button>

            <Button
              variant="secondary"
              onClick={showHelpModal}
              className="flex-1"
            >
              I Need Assistance
            </Button>

            {!checkinToday && !currentLocation && (
              <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-amber-800 text-sm font-medium mb-2">
                  📍 Location access is required to check in
                </p>
                <p className="text-amber-700 text-xs">
                  Your location helps verify you're at the correct job site. If you're having trouble with location services, please contact the office for assistance.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </ScrollArea>
  );
}
